/**
 * Componente CalendarTemplate
 * Visualizza un calendario mensile con i dati di presenza dell utente.
 * Permette la navigazione tra mesi e la compilazione del report mensile.
 *
 * Props:
 * - onCompileReport: funzione chiamata per compilare il report del mese selezionato
 */
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWeekend,
  parse,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { useCallback, useMemo } from "react";
import {
  useGetPresence,
  useQueryPresencePKSKPrefix,
} from "../../hooks/data/use-presence-data";
import { useUserId } from "../../hooks/useUserId";
import {
  MonthStringFormat,
  type Expense,
  type Mileage,
  type Presence,
  type PresenceMonth,
} from "../../types/presence";
import CalendarBox from "./CalendarBox";
import CalendarMonthNav from "./CalendarMonthNav";
import Typography from "../Typography";

/**
 * Props per CalendarTemplate
 */
type CalendarProps = {
  month: number;
  year: number;
  onVisibleMonthChange(year: number, month: number): void;
  onCompileReport(year: number, month: number): void;
};

/**
 * Componente principale del calendario mensile presenze.
 */
export default function CaldendarTemplate(props: CalendarProps) {
  const { month, year, onVisibleMonthChange } = props;
  // ID utente autenticato
  const userId = useUserId();

  // Calcola i giorni di inizio/fine mese e settimana per il calendario
  const { firstDayOfMonth, startDay, endDay } = useMemo(() => {
    const date = parse(`${year}-${month}-01`, "yyyy-MM-dd", new Date());
    const day = startOfMonth(date);
    const startDay = startOfWeek(day, {
      weekStartsOn: 1,
    });
    const lastDayInCurrentMonth = endOfMonth(day);
    const endDay = endOfWeek(lastDayInCurrentMonth, {
      weekStartsOn: 1,
    });
    return {
      firstDayOfMonth: day,
      startDay,
      endDay,
      lastDayOfMonth: lastDayInCurrentMonth,
    };
  }, [year, month]);

  // Stringa mese/anno per query DynamoDB
  const monthString = format(firstDayOfMonth, MonthStringFormat);
  // Query per i dati giornalieri di presenza
  const { data: presenceData, isLoading: isLoadingPresence } =
    useQueryPresencePKSKPrefix<Presence>(
      `${userId}`,
      `presenceDay#${monthString}`,
    );

  const { data: expenseData, isLoading: isLoadingExpense } =
    useQueryPresencePKSKPrefix<Expense>(`${userId}`, `expense#${monthString}`);

  const { data: mileageData, isLoading: isLoadingMileage } =
    useQueryPresencePKSKPrefix<Mileage>(`${userId}`, `mileage#${monthString}`);

  const daysData = useMemo(() => {
    return [
      ...(presenceData || []),
      ...(expenseData || []),
      ...(mileageData || []),
    ];
  }, [presenceData, expenseData, mileageData]);

  const isLoadingDays =
    isLoadingPresence || isLoadingExpense || isLoadingMileage;

  // Query per i dati mensili di presenza
  const { data: monthData, isLoading: isLoadingMonth } =
    useGetPresence<PresenceMonth>(`${userId}`, `presenceMonth#${monthString}`);

  // Costruisce la lista di box del calendario (uno per ogni giorno visualizzato)
  const boxes = useMemo(
    () =>
      eachDayOfInterval({ start: startDay, end: endDay }).map((currDay) => {
        const weekend = isWeekend(currDay);
        const isDifferentMonth = !isSameMonth(currDay, firstDayOfMonth);
        // Cerca i dati di presenza per il giorno corrente
        const dayItems = daysData?.filter((item) =>
          isSameDay(item.date, currDay),
        );
        return {
          weekend,
          isDifferentMonth,
          currDay,
          dayItems,
        };
      }),
    [daysData, firstDayOfMonth, startDay, endDay],
  );

  // Gestore cambio mese/anno dalla navbar
  const handlerMonthChange = useCallback(
    (year: number, month: number) => {
      onVisibleMonthChange(year, month);
    },
    [onVisibleMonthChange],
  );
  // Disabilita la compilazione report se gia inviato
  const onCompileReport =
    !isLoadingMonth && !!monthData?.sendDate
      ? undefined
      : props.onCompileReport;
  return (
    <div className="CaldendarTemplate">
      {/* Navbar per selezione mese/anno e invio report */}
      <CalendarMonthNav
        isLoading={isLoadingMonth || isLoadingDays}
        month={month}
        year={year}
        onMonthChange={handlerMonthChange}
        onCompileReport={onCompileReport}
      />
      <div className="CalendarTemplate__calendar-wrapper">
        {/* Intestazione giorni della settimana */}
        <div className="CaldendarTemplate__days">
          <div className="CaldendarTemplate__day">Lun</div>
          <div className="CaldendarTemplate__day">Mar</div>
          <div className="CaldendarTemplate__day">Mer</div>
          <div className="CaldendarTemplate__day">Gio</div>
          <div className="CaldendarTemplate__day">Ven</div>
          <div className="CaldendarTemplate__day">Sab</div>
          <div className="CaldendarTemplate__day">Dom</div>
        </div>
        {/* Griglia giorni del mese */}
        <div className="CaldendarTemplate__grid">
          {boxes.map(({ weekend, isDifferentMonth, currDay, dayItems }) => (
            <CalendarBox
              key={currDay.toString()}
              disable={
                weekend ||
                isDifferentMonth ||
                isLoadingMonth ||
                isLoadingDays ||
                !!monthData?.sendDate
              }
              items={dayItems}
              day={currDay}
            />
          ))}
        </div>
      </div>
      {/* Legenda indicatori mobile */}
      <div className="CaldendarTemplate__legend">
        <ul>
          <li>
            <span className="CaldendarTemplate__legend__indicatore CaldendarTemplate__legend__indicatore-ferie"></span>
            <Typography variant="body2">Ferie</Typography>
          </li>
          <li>
            <span className="CaldendarTemplate__legend__indicatore CaldendarTemplate__legend__indicatore-festività"></span>
            <Typography variant="body2">Festività</Typography>
          </li>
          <li>
            <span className="CaldendarTemplate__legend__indicatore CaldendarTemplate__legend__indicatore-chiusura-ufficio"></span>
            <Typography variant="body2">Chiusura ufficio</Typography>
          </li>
          <li>
            <span className="CaldendarTemplate__legend__indicatore CaldendarTemplate__legend__indicatore-permesso"></span>
            <Typography variant="body2">Permesso</Typography>
          </li>
          <li>
            <span className="CaldendarTemplate__legend__indicatore CaldendarTemplate__legend__indicatore-malattia"></span>
            <Typography variant="body2">Malattia</Typography>
          </li>
          <li>
            <span className="CaldendarTemplate__legend__indicatore CaldendarTemplate__legend__indicatore-maternità"></span>
            <Typography variant="body2">Maternità</Typography>
          </li>
          <li>
            <span className="CaldendarTemplate__legend__indicatore CaldendarTemplate__legend__indicatore-104"></span>
            <Typography variant="body2">Permesso 104</Typography>
          </li>
        </ul>
      </div>
    </div>
  );
}
